import {
    Box,
    IconButton,
    Radio,
    TableCell,
    TableRow,
    Theme,
    Typography,
    CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { TableHeaderData } from "../investorOverview/types";
import theme from "../../themes/default";
import type { MemberRow } from "../../redux-store/types/investorProfile";
import { USER_ROLES } from "../../utils/constant";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootStateType } from "../../redux-store/reducers";
import { isRMLikeRole } from "../../utils/utilityFunctions";

export function FamilyMembersTableHeader(
    isAddFamilyMemberFlow: boolean
): TableHeaderData[] {
    return [
        { id: "sno", label: "S. NO", align: "center", isSortable: false },
        { id: "name", label: "NAME", align: "center", isSortable: false },
        {
            id: "clientCode",
            label: "INVESTOR CODE",
            align: "center",
            isSortable: false,
        },
        {
            id: "investorPan",
            label: "INVESTOR PAN",
            align: "center",
            isSortable: false,
        },
        {
            id: "phoneNumber",
            label: "PHONE NUMBER",
            align: "center",
            isSortable: false,
        },
        { id: "email", label: "EMAIL", align: "center", isSortable: false },
        ...(!isAddFamilyMemberFlow
            ? [
                  {
                      id: "headOfFamily",
                      label: "HEAD OF FAMILY",
                      align: "center" as const,
                      isSortable: false,
                  },
              ]
            : []),
        {
            id: "consentStatus",
            label: "CONSENT STATUS",
            align: "center",
            isSortable: false,
        },
        { id: "action", label: "ACTION", align: "center", isSortable: false },
    ];
}

export interface CreateFamilyMembersTableBodyProps {
    data: MemberRow[];
    isAddFamilyMemberFlow: boolean;
    headOfFamilyId: string | null;
    isHofVerified: boolean;
    onHeadOfFamilyChange: (memberId: string, memberName: string) => void;
    onSendOtp: (memberId: string) => void;
    onRemoveMember: (memberId: string) => void;
    isOtpLoading: string | null;
}

export default function CreateFamilyMembersTableBody({
    data,
    isAddFamilyMemberFlow,
    headOfFamilyId,
    isHofVerified,
    onHeadOfFamilyChange,
    onSendOtp,
    onRemoveMember,
    isOtpLoading,
}: CreateFamilyMembersTableBodyProps): JSX.Element {
    const history = useHistory();
    const { role = "" } = useSelector((state: RootStateType) => state.auth);
    const { pathname} = history.location;
    return (
        <>
            {data.map((row, index) => {
                const radioCheckCondition = (role === USER_ROLES.INVESTOR) && index == 0 ? true : headOfFamilyId === row.id;
                const isInvestorFamilyRoute = pathname.includes('/investor-family/createFamily');
                return (
                <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:last-child td": { border: 0 } }}
                >
                    <TableCell
                        align="center"
                        sx={{
                            ...theme.typography.body_500,
                            borderBottom: (t: Theme) =>
                                `1px solid ${t.palette.grey[100]}`,
                        }}
                    >
                        {String(index + 1).padStart(2, "0")}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={{
                            ...theme.typography.body_500,
                            borderBottom: (t: Theme) =>
                                `1px solid ${t.palette.grey[100]}`,
                        }}
                    >
                        {row.name}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={{
                            ...theme.typography.body_500,
                            borderBottom: (t: Theme) =>
                                `1px solid ${t.palette.grey[100]}`,
                        }}
                    >
                        {row.id}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={{
                            ...theme.typography.body_500,
                            borderBottom: (t: Theme) =>
                                `1px solid ${t.palette.grey[100]}`,
                        }}
                    >
                        {row.investorPan}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={{
                            ...theme.typography.body_500,
                            borderBottom: (t: Theme) =>
                                `1px solid ${t.palette.grey[100]}`,
                        }}
                    >
                        {row.phoneNumber}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={{
                            ...theme.typography.body_500,
                            borderBottom: (t: Theme) =>
                                `1px solid ${t.palette.grey[100]}`,
                        }}
                    >
                        {row.email}
                    </TableCell>
                    {!isAddFamilyMemberFlow && (
                        <TableCell
                            align="center"
                            sx={{
                                borderBottom: (t: Theme) =>
                                    `1px solid ${t.palette.grey[100]}`,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                }}
                            >
                                <Radio
                                    size="small"
                                    checked={radioCheckCondition}
                                    disabled={isInvestorFamilyRoute || (isHofVerified && !isAddFamilyMemberFlow)}
                                    onChange={() => {
                                        onHeadOfFamilyChange(row.id, row.name);
                                    }}
                                    sx={{
                                        padding: "0px",
                                        color:
                                            theme.palette.customColors
                                                ?.checkBoxUnchecked ??
                                            theme.palette.grey[700],
                                        "&.Mui-checked": {
                                            color:
                                                theme.palette.primary[500] ??
                                                theme.palette.primary.main,
                                        },
                                    }}
                                />
                            </Box>
                        </TableCell>
                    )}
                    <TableCell
                        align="center"
                        sx={{
                            ...theme.typography.body_500,
                            borderBottom: (t: Theme) =>
                                `1px solid ${t.palette.grey[100]}`,
                        }}
                    >
                            {((row.consentStatus === "sendOtp" || !row.consentStatus)) && (
                                <>
                                    {(isInvestorFamilyRoute && radioCheckCondition) || (!isHofVerified && !radioCheckCondition && isRMLikeRole(role) && !isAddFamilyMemberFlow)? (
                                        <Typography
                                            sx={{
                                                ...theme.typography.body_500,
                                                color: theme.palette.text.secondary,
                                            }}
                                        >
                                            {""}
                                        </Typography>
                                    ) : (
                            <Typography
                                component="button"
                                type="button"
                                onClick={() => onSendOtp(row.id)}
                                sx={{
                                    ...theme.typography.body_500,
                                    color: theme.palette.primary[500],
                                    bgcolor: "transparent",
                                    border: "none",
                                    cursor: isOtpLoading ? "not-allowed" : "pointer",
                                    textDecoration: "underline",
                                    "&:hover": { opacity: 0.8 },
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "80px", 
                                    height: "24px",
                                }}
                            >
                                {isOtpLoading == row.id ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    "Send OTP"
                                )}
                            </Typography>
                                    )}
                                </>
                        )}
                        {row.consentStatus === "verified" && (
                            <Typography
                                sx={{
                                    ...theme.typography.body_500,
                                    color: theme.palette.success[500],
                                }}
                            >
                                Verified
                            </Typography>
                        )}
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={{
                            borderBottom: (t: Theme) =>
                                `1px solid ${t.palette.grey[100]}`,
                        }}
                    >
                        {!(isInvestorFamilyRoute && radioCheckCondition) && !(isHofVerified && radioCheckCondition && !isAddFamilyMemberFlow) && (
                            <IconButton
                                onClick={() => onRemoveMember(row.id)}
                                sx={{ color: theme.palette.error[500] }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        )}
                    </TableCell>
                </TableRow>
            )})}
        </>
    );
}
